/* eslint-disable react/jsx-boolean-value */
import PropTypes from "prop-types";
import { generateTruncatedString, getFirstLetters, getFiveStarRating } from "../../../utils/common";
import RatingStars from "../../molecules/ratingStars";
import CustomImage from "../../atoms/customImage";
import { getTimeAgo } from "../../../utils/common/timeSlot";
import { READ_MORE_WITHOUT_DOTS } from "../../../constants/common/common";

/**
 * Component for displaying a testimonial card.
 * @param {object} props - The props object.
 * @param {object} props.review - The review object containing reviewer details.
 * @returns {JSX.Element} - Testimonial card component.
 */
const TestimonialCard = ({ review, isShowImage = false, onClickCard = () => null, charBeforeTruncate = 0 }) => {
  const ratingValue = getFiveStarRating(review?.overallRating);

  return (
    <div
      className={`flex h-full w-full flex-wrap justify-between rounded-xl border-1 border-thinLightGrey bg-white-900 p-16 ${charBeforeTruncate > 0 ? "hover:cursor-pointer" : ""} md:flex-nowrap`}
      itemProp="review"
      itemScope={true}
      itemType="https://schema.org/Review"
      onClick={onClickCard}
    >
      <div className={isShowImage && review?.productImageLink ? "w-full md:w-[calc(100%-126px)]" : "w-full"}>
        <div className="mb-16 flex w-full items-center">
          <div className="flex h-44 w-44 items-center justify-center rounded-full bg-grey-700 uppercase text-fnp-100">
            {getFirstLetters(review?.reviewerName ? review?.reviewerName : "Anonymous")}
          </div>
          <div className="ml-10 w-[calc(100%-3.75rem)]">
            <div className="mt-4 flex w-full items-center gap-4 truncate text-12">
              <span className="max-w-[50%] overflow-hidden text-ellipsis whitespace-nowrap text-14 font-500 text-fnp-500 md:max-w-[60%]">
                <div itemScope itemProp="author" itemType="https://schema.org/Person">
                  <meta itemProp="name" content={review?.reviewerName ? review?.reviewerName : "Anonymous"} />
                </div>
                {review?.reviewerName ? review?.reviewerName : "Anonymous"}
              </span>
              <span className="h-[4px] w-[4px] flex-none rounded-full bg-fnp-500" />
              <span className="text-12 text-gray-500">
                {review?.respondedDateMillis && (
                  <meta itemProp="datePublished" content={new Date(review.respondedDateMillis).toISOString()} />
                )}
                {getTimeAgo(review?.respondedDateMillis)}
              </span>
            </div>
            <div itemProp="reviewRating" itemScope={true} itemType="https://schema.org/Rating">
              <meta itemProp="ratingValue" content={String(ratingValue)} />
              <meta itemProp="bestRating" content="5" />
              <meta itemProp="worstRating" content="1" />
              <RatingStars ratings={ratingValue} stars={5} />
            </div>
          </div>
        </div>
        <div
          className={`flex ${charBeforeTruncate > 0 ? "h-[50px]" : ""} flex-wrap gap-8 overflow-hidden`}
          itemProp="description"
        >
          {charBeforeTruncate && review?.reviewText?.length > charBeforeTruncate ? (
            <div className="inline">
              <p className="inter inline text-14 font-400 leading-5 text-grey-500">{`${generateTruncatedString(review?.reviewText, charBeforeTruncate)}`}</p>
              <div className="ml-4 inline-block align-middle">
                <p className="text-14 font-600 leading-5 text-grey-500">{READ_MORE_WITHOUT_DOTS}</p>
              </div>
            </div>
          ) : (
            <p className="text-14 text-grey-500">{review?.reviewText}</p>
          )}
        </div>
        <div className="flex w-full items-center justify-between">
          <div className="mt-16 flex flex-wrap gap-8 font-500">
            {review?.deliveryOccasion && (
              <div className="w-auto max-w-[12.5rem] rounded-md bg-grey-100 p-4 px-6 text-12  font-500 capitalize leading-5 text-grey-75">
                Occasion:<span className="ml-4">{review?.deliveryOccasion}</span>
              </div>
            )}
            {review?.deliveryCity && (
              <div className="w-auto max-w-[12.5rem] rounded-md bg-grey-100 p-4 px-6 text-12 font-500 capitalize leading-5 text-grey-75">
                City:<span className="ml-4">{review?.deliveryCity}</span>
              </div>
            )}
          </div>
        </div>
      </div>
      {isShowImage && review?.productImageLink && (
        <div className="mt-8 w-full md:mt-0 md:w-auto">
          <CustomImage
            src={process.env.NEXT_PUBLIC_ASSET_PREFIX + review?.productImageLink}
            alt={review?.productAlternateText}
            width={126}
            height={126}
            isPriority={false}
            borderRadius={8}
          />
        </div>
      )}
    </div>
  );
};

TestimonialCard.propTypes = {
  review: PropTypes.shape({
    reviewerName: PropTypes.string.isRequired,
    overallRating: PropTypes.number.isRequired,
    respondedDateMillis: PropTypes.number.isRequired,
    reviewText: PropTypes.string,
    deliveryOccasion: PropTypes.string,
    deliveryCity: PropTypes.string.isRequired,
    productImageLink: PropTypes.string,
    productAlternateText: PropTypes.string,
  }).isRequired,
  isShowImage: PropTypes.bool,
  charBeforeTruncate: PropTypes.number,
  onClickCard: PropTypes.func,
};

export default TestimonialCard;
